<template>
  <div class="pw-dashboard">
    <NotificationPanel />
    <Confirm />
    <Loading v-if="!loaded" @loaded="(state) => (loaded = state)" />
    <Navbar v-if="loaded" :minimalNavbar="minimalNavbar" :organization="organization">
      <slot name="account-profile" slot="account-profile"></slot>
    </Navbar>
    <Notifications v-if="loaded" />
    <Sidebar v-if="loaded" />
    <div class="pw-content" v-if="loaded && loadedClaims">
      <slot />
    </div>
  </div>
</template>

<script>
import Navbar from './Navbar.vue';
import Sidebar from './Sidebar.vue';
import Loading from './Loading.vue';
import Confirm from './Confirm.vue';
import NotificationPanel from './NotificationPanel.vue';
import ApiClient from '../utils/ApiClient';

export default {
  name: 'Dashboard',
  props: {
    module: {
      type: String,
      default: '',
    },
    minimalNavbar: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Navbar,
    Sidebar,
    Loading,
    Confirm,
    NotificationPanel,
  },
  data() {
    return {
      loaded: false,
      loadedClaims: false,
      organization: null,
    };
  },
  created() {
    console.log('Dashboard');
    if (this.module !== '') {
      this.getUserOrganization();
    } else {
      this.loadedClaims = true;
    }
  },
  methods: {
    getClaims() {
      console.log('claims', localStorage.getItem('OCode'));
      const apiClient = new ApiClient('Security/userClaims', 'stamp');
      apiClient.Get(this.module).then((claims) => {
        const abilities = [];

        claims.forEach((claim) => {
          const ability = claim.replace(`${this.module}.`, '');
          abilities.push(ability);
        });

        this.$ability.update([{ subject: this.module, actions: abilities }]);
        this.$root.$emit('claimsLoaded');
        this.loadedClaims = true;
      });
    },
    async getUserOrganization() {
      const apiClient = new ApiClient('Configuration/UserOrganizationActive');
      apiClient.Get().then((res) => {
        this.organization = res;
        localStorage.setItem('OCode', this.organization.organizationCode);
        this.getClaims();
        this.$root.$on('reloadClaims', this.getClaims);
      });
    },
  },
};
</script>

<style lang="scss">
body {
  margin: 0;
  font-family: 'Nunito Sans';
  overflow-x: hidden;
  &.pw-dashboard--collapsed {
    overflow: hidden;
  }
  * {
    font-family: 'Nunito Sans';
  }
}

hr {
  background-color: #d8e3eb;
  border: 0;
  height: 1px;
  margin-top: 0;
}

.pw-content {
  transition: all 0.3s ease-in-out;

  margin-top: 55px;
  padding-top: 32px;
  padding-bottom: 50px;
  padding-right: 40px;
  padding-left: 40px;
  box-sizing: border-box;
  overflow-y: unset;
}

@media all and (max-width: 768px) {
  .pw-content {
    margin-left: 0;
  }
}

@media (max-width: 667px) {
  .pw-content {
    padding-right: 16px;
    padding-left: 16px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

$main: #5186ff;

$primary: #6ae1c8;
$primary-hover: #6ed2bd;
$primary-selected: #51c6ae;
$primary-disabled: #97decf;

$secondary: #5b89f2;
$secondary-hover: #5d86e1;
$secondary-selected: #5079d8;
$secondary-disabled: #8cabf3;

$terciary: #707070;
$terciary-hover: #a7b1b8;
$terciary-selected: #a0a4a7;
$terciary-disabled: #c2c2c2;

$quinary: #f25555;
$quinary-hover: #e14f4f;
$quinary-selected: #e54040;
$quinary-disabled: #ec9696;

$success: #19bf95;
$success-hover: #19bf95;
$success-selected: #19bf95;
$success-disabled: #19bf95;

$color1: #9f9f9f;
$color2: #ffc848;
$color3: #6ae1c8;
$color4: #19bf95;
$color5: #5186ff;
$color6: #707070;
$color7: #f25555;
$color8: #9c50c8;
$color9: #ff9b43;

$list: 'primary', 'secondary', 'terciary', 'quinary', 'success', 'color1', 'color2', 'color3',
  'color4', 'color5', 'color6', 'color7', 'color8', 'color9';

$colors: (
  primary: $primary,
  secondary: $secondary,
  terciary: $terciary,
  quinary: $quinary,
  success: $success,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
  color8: $color8,
  color9: $color9,
);

$colors-hover: (
  primary: $primary-hover,
  secondary: $secondary-hover,
  terciary: $terciary-hover,
  quinary: $quinary-hover,
  success: $success-hover,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
  color8: $color8,
  color9: $color9,
);

$colors-selected: (
  primary: $primary-selected,
  secondary: $secondary-selected,
  terciary: $terciary-selected,
  quinary: $quinary-selected,
  success: $success-selected,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
  color8: $color8,
  color9: $color9,
);

$colors-disabled: (
  primary: $primary-disabled,
  secondary: $secondary-disabled,
  terciary: $terciary-disabled,
  quinary: $quinary-disabled,
  success: $success-disabled,
  color1: $color1,
  color2: $color2,
  color3: $color3,
  color4: $color4,
  color5: $color5,
  color6: $color6,
  color7: $color7,
  color8: $color8,
  color9: $color9,
);

@mixin variantColors($outlined: false) {
  @each $var in $list {
    @if $outlined {
      &.is-#{$var}-outlined {
        border-color: map-get($colors, $var);
        color: map-get($colors, $var);
        background-color: transparent;
        &:hover {
          border-color: map-get($colors-hover, $var);
        }
        &:focus,
        &:active {
          border-color: map-get($colors-selected, $var);
        }
      }
    }
    &.is-#{$var} {
      background-color: map-get($colors, $var);
      border-color: map-get($colors, $var);
      color: white;
      &:hover {
        background-color: map-get($colors-hover, $var);
      }
      &:focus,
      &:active {
        background-color: map-get($colors-selected, $var);
      }
      &:disabled {
        background-color: map-get($colors-disabled, $var);
      }
    }
  }
}

hr {
  background-color: #d8e3eb;
  border: 0;
  height: 1px;
  margin-top: 0;
  margin-block-end: 16px;
}

.pw-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  color: #333;
  line-height: 1.33;
  border: 1px solid transparent;
  pointer-events: none;
  @include variantColors(true);
  &--level-0 {
    background-color: #8f5ef1;
    color: white;
  }
  &--level-1 {
    background-color: #7b8bfb;
    color: black;
  }
  &--level-2 {
    background-color: #d56ceb;
    color: black;
  }
  &--level-3 {
    background-color: #de6c6a;
    color: black;
  }
  &--level-4 {
    color: black;
    background-color: #ffc787;
  }
}
</style>
